import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ChevronRight, Sparkles, Zap, TrendingUp, Lock, Briefcase, CheckCircle, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import api from '../utils/api';
import { GroupedOfficeMenu } from '../components/GroupedOfficeMenu';
import { getCachedLogo, cacheLogo } from '../utils/logo';

// lucide-reactアイコンのマップ（管理画面で選べるアイコンを列挙）
const ICON_MAP = {
  Sparkles, Zap, TrendingUp, Lock, Briefcase, CheckCircle, Gift,
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [newsItems, setNewsItems] = useState([]);
  const [recruitmentItems, setRecruitmentItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState(null); // 展開中のメニュー（ヘッダー）
  const [expandedFooterMenu, setExpandedFooterMenu] = useState(null); // 展開中のフッターメニュー
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // メニュー外をクリックしたら閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedMenu && !event.target.closest('nav')) {
        setExpandedMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedMenu]);

  // 事業所カードへスムーズスクロール
  const scrollToOffice = (officeId) => {
    const el = document.getElementById(`office-${officeId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 他ページから /#office-xxx で来た場合、読み込み後にスクロール
  useEffect(() => {
    if (!loading && offices.length > 0 && window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    }
  }, [loading, offices]);

  useEffect(() => {
    // ニュース記事、人事募集、運営事業所、会社概要、ナビゲーションを取得
    // 1つのAPIが失敗しても他の表示が消えないよう allSettled を使用
    const fetchData = async () => {
      // 各リクエストにタイムアウトを設定し、応答なしは自動リトライ（api.js）
      // 同一URLの同時多重発行はapi.js側で1本に集約される
      const opts = { timeout: 6000 };
      const results = await Promise.allSettled([
        api.get('/news/published', opts),  // 公開済みニュースのみ取得
        api.get('/recruitment/active', opts),
        api.get('/offices/active', opts),
        api.get('/company-info', opts),
        api.get('/navigation', opts),
        api.get('/features/active', opts),
        api.get('/offices/group-order', opts),
      ]);
      const [news, recruitment, officesRes, company, navigation, featuresRes, groupOrderRes] = results;
      if (news.status === 'fulfilled') setNewsItems(news.value.data);
      if (recruitment.status === 'fulfilled') setRecruitmentItems(recruitment.value.data);
      if (officesRes.status === 'fulfilled') setOffices(officesRes.value.data);
      if (company.status === 'fulfilled') { setCompanyInfo(company.value.data); cacheLogo(company.value.data?.logo_url); }
      if (navigation.status === 'fulfilled') setNavigationItems(navigation.value.data);
      if (featuresRes.status === 'fulfilled') setFeatures(featuresRes.value.data);
      if (groupOrderRes.status === 'fulfilled') setGroupOrder(groupOrderRes.value.data.order || []);
      results.forEach((r) => { if (r.status === 'rejected') console.error('データ取得エラー:', r.reason); });
      setLoading(false);
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ヘッダー */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* ロゴと言語切り替え */}
          <div className="flex justify-between items-center py-3 sm:py-4 gap-2">
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="relative flex-shrink-0 h-[51px] sm:h-[72px] flex items-center">
                <img
                  src={companyInfo?.logo_url || getCachedLogo()}
                  alt="Presage Corporation"
                  className="h-full w-auto object-contain"
                  data-testid="header-logo-img"
                />
              </div>
              <div className="flex flex-col justify-center min-w-0">
                <span className="text-sm sm:text-xl font-bold text-gray-900 truncate leading-tight">
                  Presage Corporation
                </span>
                <span className="text-[10px] sm:text-xs text-amber-500 truncate leading-tight">株式会社プレサージュ</span>
              </div>
            </Link>
            
            {/* 言語切り替え */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageSwitcher />
            </div>
          </div>

          {/* ナビゲーション（常時表示、狭い画面は折り返し） */}
          <nav
            className="border-t border-b border-amber-500/20 py-[11px] sm:py-[14px]"
            data-testid="main-nav"
          >
            <ul className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 sm:gap-x-6 md:gap-x-8 px-2">
              {navigationItems
                .filter(item => !item.parent_id && item.path !== '/privacy')
                .map((item) => {
                  const subItems = navigationItems.filter(sub => sub.parent_id === item.id);
                  const isOfficesMenu = item.path === '/operating-office' && offices.length > 0;
                  const hasSubMenu = subItems.length > 0 || isOfficesMenu;
                  const isExpanded = expandedMenu === item.id;
                  
                  return (
                    <li key={item.path} className="relative">
                      {hasSubMenu ? (
                        // サブメニューがある場合：クリックで展開
                        <button
                          onClick={() => setExpandedMenu(isExpanded ? null : item.id)}
                          className="text-sm text-gray-600 hover:text-amber-500 transition-colors duration-300 relative group flex items-center"
                          data-testid={isOfficesMenu ? 'nav-offices-button' : undefined}
                        >
                          {item.label}
                          <span className="ml-1 transition-transform duration-300" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                        </button>
                      ) : (
                        // サブメニューがない場合：通常のリンク
                        <Link
                          to={item.path}
                          className="text-sm text-gray-600 hover:text-amber-500 transition-colors duration-300 relative group"
                        >
                          {item.label}
                          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                        </Link>
                      )}

                      {/* 運営事業所：各事業所へスクロール */}
                      {isOfficesMenu && isExpanded && (
                        <GroupedOfficeMenu offices={offices} groupOrder={groupOrder} onNavigate={() => setExpandedMenu(null)} />
                      )}
                      
                      {/* 通常のサブメニュー */}
                      {!isOfficesMenu && hasSubMenu && isExpanded && (
                        <ul className="absolute left-0 top-full mt-2 w-56 bg-white/95 backdrop-blur-md border border-amber-500/30 rounded-lg shadow-lg z-50 animate-in fade-in slide-in-from-top-2 duration-300">
                          {subItems.map((subItem) => (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path}
                                onClick={() => setExpandedMenu(null)}
                                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:text-amber-500 hover:bg-amber-500/10 transition-colors first:rounded-t-lg last:rounded-b-lg"
                              >
                                {subItem.image_url && (
                                  <img 
                                    src={subItem.image_url} 
                                    alt={subItem.label}
                                    className="w-8 h-8 rounded-full object-cover border border-amber-500/30"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <span>{subItem.label}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}

              {/* 運営事業所ドロップダウン（ナビに該当項目が無い場合のみ追加） */}
              {offices.length > 0 && !navigationItems.some(i => !i.parent_id && i.path === '/operating-office') && (
                <li className="relative">
                  <button
                    onClick={() => setExpandedMenu(expandedMenu === 'offices' ? null : 'offices')}
                    className="text-sm text-gray-600 hover:text-amber-500 transition-colors duration-300 relative group flex items-center"
                    data-testid="nav-offices-button"
                  >
                    事業内容
                    <span className="ml-1 transition-transform duration-300" style={{ transform: expandedMenu === 'offices' ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                  </button>
                  {expandedMenu === 'offices' && (
                    <GroupedOfficeMenu offices={offices} groupOrder={groupOrder} onNavigate={() => setExpandedMenu(null)} />
                  )}
                </li>
              )}
            </ul>
          </nav>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 bg-white">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-amber-500">Disability welfare services</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-black animate-fade-in break-words">
            {t('home.hero.title')}
          </h1>
          
          <p className="text-base sm:text-xl md:text-2xl text-gray-600 mb-10 sm:mb-12 max-w-3xl mx-auto px-2">
            {t('home.hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link to="/contact" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-full sm:w-auto">
              <Button variant="outline" className="border-2 border-amber-500 text-amber-500 hover:bg-amber-500/10 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-lg backdrop-blur-sm transition-all duration-300 w-full sm:w-auto" data-testid="hero-contact-btn">
                {t('home.hero.cta2')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 特徴セクション */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="mt-[-19mm] text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              私たちの強み
            </h2>
            <p className="mt-[2mm] text-gray-600 text-lg">革新的なソリューションで未来を創造</p>
          </div>

          <div className="mt-[-10mm] grid md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = ICON_MAP[feature.icon_name] || Sparkles;
              return (
                <button
                  key={feature.id}
                  type="button"
                  onClick={() => setSelectedFeature(feature)}
                  data-testid={`feature-card-${feature.id}`}
                  className="group relative p-8 rounded-2xl bg-gradient-to-br from-gray-50/50 to-gray-100/50 border border-amber-500/20 backdrop-blur-sm hover:border-amber-500/50 transition-all duration-500 transform hover:-translate-y-2 text-left cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-amber-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                    <span className="inline-flex items-center mt-4 text-amber-500 text-sm group-hover:text-amber-500 transition-colors">
                      詳しく見る
                      <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 特徴詳細モーダル */}
        <Dialog open={!!selectedFeature} onOpenChange={(open) => !open && setSelectedFeature(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/40 text-gray-900" data-testid="feature-dialog">
            {selectedFeature && (() => {
              const Icon = ICON_MAP[selectedFeature.icon_name] || Sparkles;
              return (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-4 mb-2">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-gray-900" />
                      </div>
                      <DialogTitle className="text-2xl sm:text-3xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                        {selectedFeature.title}
                      </DialogTitle>
                    </div>
                  </DialogHeader>
                  <p className="text-amber-500 text-lg mb-4">{selectedFeature.description}</p>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedFeature.content}
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </section>

      {/* Latest News セクション */}
      <section className="mt-[-20mm] py-20 relative">
        <div className="scale-[0.7] origin-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-[-10mm] flex justify-between items-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              Latest News
            </h2>
            <Link 
              to="/news" 
              className="text-amber-500 hover:text-amber-500 flex items-center space-x-2 group transition-colors"
            >
              <span>VIEW ALL</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-full text-center text-gray-600">読み込み中...</div>
            ) : newsItems.length === 0 ? (
              <div className="mt-[-10mm] col-span-full text-center text-gray-600">ニュースがありません</div>
            ) : (
              newsItems.map((news) => (
                <Link
                  to={`/news/${news.id}`}
                  key={news.id}
                  data-testid={`news-card-link-${news.id}`}
                  className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500 transform hover:-translate-y-2 block"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-600/0 group-hover:from-amber-500/10 group-hover:to-amber-600/10 transition-all duration-500"></div>
                  
                  {news.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={news.image_url}
                        alt={news.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-50 to-transparent"></div>
                    </div>
                  )}

                  <div className="relative p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="px-3 py-1 bg-amber-500/20 text-amber-500 text-xs rounded-full border border-amber-500/30">
                        {news.category}
                      </span>
                      <span className="text-gray-500 text-xs">{news.date}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-amber-500 transition-colors">
                      {news.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {news.excerpt}
                    </p>
                    
                    <span className="inline-flex items-center text-amber-500 group-hover:text-amber-500 transition-colors">
                      続きを読む
                      <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
        </div>
      </section>

      {/* 運営事業所セクション */}
      {offices.length > 0 && (
        <section className="py-20 relative">
          <div className="mt-[-30mm] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="mt-[-10mm] text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                運営事業所
              </h2>
              <p className="text-gray-600 text-lg">私たちの拠点をご紹介します</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {offices.map((office) => (
                <div 
                  key={office.id}
                  id={`office-${office.id}`}
                  className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500 scroll-mt-40"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-600/0 group-hover:from-amber-500/10 group-hover:to-amber-600/10 transition-all duration-500"></div>
                  
                  {office.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={office.image_url} 
                        alt={office.name} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-50 to-transparent"></div>
                    </div>
                  )}
                  
                  <div className="relative p-6">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-amber-500 transition-colors mb-3">
                      {office.name}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 text-sm">{office.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start space-x-2 text-gray-600">
                        <MapPin className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
                        <span>{office.address}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{office.phone}</span>
                      </div>
                      
                      {office.email && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <span>{office.email}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{office.business_hours}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 採用情報セクション */}
      {recruitmentItems.length > 0 && (
        <section className="py-20 relative bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                採用情報
              </h2>
              <p className="text-gray-600 text-lg">私たちと一緒に働きませんか？</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {recruitmentItems.map((job) => (
                <div 
                  key={job.id}
                  className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-600/0 group-hover:from-amber-500/10 group-hover:to-amber-600/10 transition-all duration-500"></div>
                  
                  <div className="relative p-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 group-hover:text-amber-500 transition-colors">
                        {job.title}
                      </h3>
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                        募集中
                      </span>
                    </div>
                    
                    <div className="rich-content text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: job.description }} />
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-500 mb-1">勤務地</h4>
                          <p className="text-gray-600">{job.location}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Briefcase className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-500 mb-1">雇用形態</h4>
                          <p className="text-gray-600">{job.employment_type}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-500 mb-1">応募資格</h4>
                          <div className="rich-content text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: job.requirements }} />
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Gift className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-500 mb-1">待遇・福利厚生</h4>
                          <div className="rich-content text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: job.benefits }} />
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
                      onClick={() => navigate(`/apply?job_id=${job.id}&job_title=${encodeURIComponent(job.title)}`)}
                    >
                      この求人に応募する
                      <ChevronRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* フッター */}
      <footer id="contact" className="relative border-t border-amber-500/20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mt-[-10mm] grid md:grid-cols-3 gap-12 mb-12">
            {/* お問い合わせ */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                お問い合わせ
              </h3>
              {companyInfo ? (
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.address}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Phone className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.phone} {companyInfo.fax && `FAX ${companyInfo.fax}`}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Mail className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Clock className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.business_hours}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">読み込み中...</p>
              )}
            </div>

            {/* メニュー */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                メニュー
              </h3>
              <ul className="space-y-3">
                {navigationItems
                  .filter(item => !item.parent_id)
                  .map((item) => {
                    const subItems = navigationItems.filter(sub => sub.parent_id === item.id);
                    const hasSubMenu = subItems.length > 0;
                    const isExpanded = expandedFooterMenu === item.id;
                    
                    return (
                      <li key={item.id}>
                        {hasSubMenu ? (
                          // サブメニューがある場合：クリックで展開
                          <button
                            onClick={() => setExpandedFooterMenu(isExpanded ? null : item.id)}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group w-full text-left"
                          >
                            <ChevronRight className={`w-4 h-4 mr-2 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
                            {item.label}
                          </button>
                        ) : (
                          // サブメニューがない場合：通常のリンク
                          <Link
                            to={item.path}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group"
                          >
                            <ChevronRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                            {item.label}
                          </Link>
                        )}
                        
                        {/* サブメニュー */}
                        {hasSubMenu && isExpanded && (
                          <ul className="ml-6 mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                            {subItems.map((subItem) => (
                              <li key={subItem.id}>
                                <Link
                                  to={subItem.path}
                                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                  className="text-gray-500 hover:text-amber-500 transition-colors duration-300 flex items-center group text-sm"
                                >
                                  <ChevronRight className="w-3 h-3 mr-2 group-hover:translate-x-1 transition-transform" />
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>

            {/* 最新情報 */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                最新情報
              </h3>
              <ul className="space-y-3">
                {newsItems.slice(0, 2).map((news) => (
                  <li key={news.id}>
                    <Link
                      to={`/news/${news.id}`}
                      className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group"
                    >
                      <ChevronRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                      {news.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* コピーライトと管理者ログイン */}
          <div className="border-t border-amber-500/20 mt-12 pt-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <img
                  src={companyInfo?.logo_url || getCachedLogo()}
                  alt="Presage Corporation"
                  className="h-9 w-auto object-contain"
                  data-testid="footer-logo-img"
                />
                <p className="text-gray-600 text-sm">
                  Copyright © 2002 {companyInfo?.name || '株式会社プレサージュ'}
                </p>
              </div>
              <Link to="/admin/login">
                <Button 
                  variant="ghost" 
                  className="text-gray-500 hover:text-amber-500 text-sm transition-colors"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  管理者ログイン
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
